import { apiService } from './apiService.js';
import logger from '../../utils/logger';

export const userApiService = {
  async getAllUsers() {
    try {
      const response = await apiService.get('/v1/user');
      return response;
    } catch (error) {
      logger.error('Error fetching all users:', error);
      throw error;
    }
  },

  async getUserById(userId) {
    try {
      const response = await apiService.get(`/v1/user/${userId}`);
      return response;
    } catch (error) {
      logger.error(`Error fetching user ${userId}:`, error);
      throw error;
    }
  },

  async signup(email, password, name) {
    try {
      const requestBody = {
        email: email,
        password: password,
        name: name,
      };
      const response = await apiService.post('/v1/user/signup', requestBody);
      return response;
    } catch (error) {
      logger.error('Error signing up user:', error);
      throw error;
    }
  },

  async login(email, password) {
    try {
      const requestBody = {
        email: email,
        password: password,
      };
      const response = await apiService.post('/v1/user/login', requestBody);
      return response;
    } catch (error) {
      logger.error('Error logging in:', error);
      throw error;
    }
  },

  async getUserInfo(email, userId = '') {
    try {
      const requestData = {
        user_email: email,
        user_id: userId
      };
      
      const response = await apiService.post('/v1/user/php-login', requestData);
      
      if (response.statusCode === 200 && Array.isArray(response.data) && response.data.length > 0) {
        const userData = response.data[0];
        
        if (userData.error === 'no_error') {
          return {
            statusCode: 201,
            data: userData,
            message: 'User authenticated successfully'
          };
        } else {
          return {
            statusCode: 401,
            data: null,
            message: userData.error || 'Authentication failed'
          };
        }
      } else {
        return {
          statusCode: 404,
          data: null,
          message: 'User not found'
        };
      }
    } catch (error) {
      logger.error('Error fetching user info:', error);
      return {
        statusCode: 500,
        data: null,
        message: error.message || 'Server error'
      };
    }
  },

  async getAppPermissions(email, userId = '', appName = '') {
    try {
      const params = new URLSearchParams();
      params.append('user_email', email);
      if (userId) {
        params.append('user_id', userId);
      }
      if (appName) {
        params.append('app_name', appName);
      }

      const response = await apiService.get(`/v1/user/app-permissions?${params.toString()}`);

      if (response.statusCode === 200 && response.data) {
        return {
          statusCode: 200,
          data: response.data,
          message: 'Success'
        };
      } else {
        return {
          statusCode: response.statusCode || 500,
          data: null,
          message: response.message || 'Failed to fetch app permissions'
        };
      }
    } catch (error) {
      logger.error('Error fetching app permissions:', error);
      return {
        statusCode: 500,
        data: null,
        message: error.message || 'Server error'
      };
    }
  },

  // ==================== デバイス管理API ====================

  async checkDevice({ user_email, app_id, device_id, device_name }) {
    try {
      const response = await apiService.post('/v1/user/device/check', {
        user_email,
        app_id,
        device_id,
        device_name,
      });
      return response;
    } catch (error) {
      logger.error('Error checking device:', error);
      return {
        statusCode: 500,
        data: null,
        message: error.message || 'Server error'
      };
    }
  },

  async unregisterDevice({ user_email, app_id, device_id }) {
    try {
      const response = await apiService.post('/v1/user/device/unregister', {
        user_email,
        app_id,
        device_id,
      });
      return response;
    } catch (error) {
      logger.error('Error unregistering device:', error);
      return {
        statusCode: 500,
        data: null,
        message: error.message || 'Server error'
      };
    }
  },

  async getUserDevices(email, appId = '') {
    try {
      const params = appId ? `?app_id=${encodeURIComponent(appId)}` : '';
      const response = await apiService.get(`/v1/user/devices/${encodeURIComponent(email)}${params}`);
      return response;
    } catch (error) {
      logger.error('Error fetching user devices:', error);
      return {
        statusCode: 500,
        data: null,
        message: error.message || 'Server error'
      };
    }
  },

  async deleteDevice(deviceId) {
    try {
      const response = await apiService.delete(`/v1/user/device/${deviceId}`);
      return response;
    } catch (error) {
      logger.error('Error deleting device:', error);
      return {
        statusCode: 500,
        data: null,
        message: error.message || 'Server error'
      };
    }
  },

  async getDevicesSummary() {
    try {
      const response = await apiService.get('/v1/user/devices-summary');
      return response;
    } catch (error) {
      logger.error('Error fetching devices summary:', error);
      return {
        statusCode: 500,
        data: null,
        message: error.message || 'Server error'
      };
    }
  },

  async getAllDevices() {
    try {
      const response = await apiService.get('/v1/user/devices-all');
      return response;
    } catch (error) {
      logger.error('Error fetching all devices:', error);
      return {
        statusCode: 500,
        data: null,
        message: error.message || 'Server error'
      };
    }
  },

  // ==================== デバイス管理 V2 API ====================
  // 注: apiService.post はNestJSの外側ラッパー {statusCode:201, data:{...}} を返すため、
  //     V2メソッドは response.data を返して呼び出し側が {statusCode:200, data:{allowed,...}} を直接扱えるようにする

  async checkDeviceV2({ user_email, app_id, device_id, device_name }) {
    try {
      const response = await apiService.post("/v1/user/device-v2/check", {
        user_email, app_id, device_id, device_name,
      });
      return response.data;
    } catch (error) {
      logger.error("Error checking device V2:", error);
      return { statusCode: 500, data: null, message: error.message || "Server error" };
    }
  },

  async verifyDeviceV2({ user_email, app_id, device_id }) {
    try {
      const response = await apiService.post("/v1/user/device-v2/verify", {
        user_email, app_id, device_id,
      });
      return response.data;
    } catch (error) {
      logger.error("Error verifying device V2:", error);
      return { statusCode: 500, data: null, message: error.message || "Server error" };
    }
  },

  async unregisterDeviceV2({ user_email, app_id, device_id }) {
    try {
      const response = await apiService.post("/v1/user/device-v2/unregister", {
        user_email, app_id, device_id,
      });
      return response.data;
    } catch (error) {
      logger.error("Error unregistering device V2:", error);
      return { statusCode: 500, data: null, message: error.message || "Server error" };
    }
  },

};
